// Das Formular eines Blocks baut sich aus der Feldbeschreibung im Blockverzeichnis.
//
// Wer einen neuen Blocktyp ergänzt, muss hier nichts ändern – nur wenn er eine
// neue Feldart braucht, kommt hier ein Fall dazu.

import { useState } from "react";
import type { FieldDef } from "@webradiosuite/shared/pages";
import { MediaPicker } from "./media-picker";

// Felder und Knöpfe nehmen die Bausteine aus index.css (.input, .btn, .field-label),
// damit Blockformulare überall – Seitenbaukasten, Module, Redaktion – gleich aussehen.
const inputClass = "input mt-1.5";
const labelClass = "block text-[0.8rem] font-medium text-secondary";
const smallBtn = "btn btn-secondary btn-sm";

type Props = Record<string, unknown>;

/**
 * Auswahllisten, die erst im Betrieb feststehen: Kategorien, Beiträge, Folgen.
 * Der Schlüssel ist die Feldart; so braucht eine weitere Liste hier keinen
 * neuen Fall mehr.
 */
export type FieldChoices = Partial<Record<"category" | "post" | "episode", Array<{ value: string; label: string }>>>;

const CHOICE_EMPTY: Record<string, string> = {
  category: "Alle Kategorien",
  post: "Bitte wählen",
  episode: "Bitte wählen",
};

interface FieldProps {
  field: FieldDef;
  value: unknown;
  onChange: (value: unknown) => void;
  channels: Array<{ slug: string; name: string }>;
  publicUrl: string;
  choices: FieldChoices;
}

/** Zeitpunkt für <input type="datetime-local"> und zurück. */
function toLocalInput(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number): string => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function ImageField({ value, onChange, publicUrl, label }: { value: string; onChange: (v: string) => void; publicUrl: string; label: string }): JSX.Element {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-1.5 flex items-center gap-3">
      {value
        ? <img src={`${publicUrl}${value}`} alt="" className="h-16 w-24 flex-shrink-0 rounded-lg border border-default object-cover" />
        : <div className="flex h-16 w-24 flex-shrink-0 items-center justify-center rounded-lg border border-dashed border-default bg-input text-xs font-normal text-muted">kein Bild</div>}
      <div className="flex flex-col items-start gap-1">
        <button type="button" onClick={() => setOpen(true)} className={smallBtn}>
          {value ? "Anderes Bild" : "Bild wählen"}
        </button>
        {value && (
          <button type="button" onClick={() => onChange("")} className="text-xs font-normal text-muted underline-offset-2 hover:text-primary hover:underline">Bild entfernen</button>
        )}
      </div>
      {open && (
        <MediaPicker
          publicUrl={publicUrl}
          onClose={() => setOpen(false)}
          onPick={(item) => { onChange(item.url); setOpen(false); }}
        />
      )}
      <span className="sr-only">{label}</span>
    </div>
  );
}

function ImageListField({ value, onChange, publicUrl }: { value: Array<{ url: string; alt: string }>; onChange: (v: Array<{ url: string; alt: string }>) => void; publicUrl: string }): JSX.Element {
  const [open, setOpen] = useState(false);
  const move = (index: number, delta: number): void => {
    const next = [...value];
    const to = index + delta;
    if (to < 0 || to >= next.length) return;
    [next[index], next[to]] = [next[to]!, next[index]!];
    onChange(next);
  };
  return (
    <div className="space-y-2">
      <ul className="mt-1.5 space-y-2">
        {value.map((img, i) => (
          <li key={i} className="flex items-center gap-2 rounded-lg border border-default bg-input p-1.5">
            <img src={`${publicUrl}${img.url}`} alt="" className="h-10 w-14 flex-shrink-0 rounded-md border border-default object-cover" />
            <input value={img.alt} onChange={(e) => onChange(value.map((it, j) => (j === i ? { ...it, alt: e.target.value } : it)))}
              placeholder="Was ist zu sehen?" aria-label={`Beschreibung Bild ${i + 1}`} className="input min-w-0 flex-1" />
            <button type="button" onClick={() => move(i, -1)} className="btn btn-ghost btn-sm btn-icon flex-shrink-0" aria-label="nach oben">↑</button>
            <button type="button" onClick={() => move(i, 1)} className="btn btn-ghost btn-sm btn-icon flex-shrink-0" aria-label="nach unten">↓</button>
            <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))} className="btn btn-ghost btn-sm btn-icon flex-shrink-0 hover:!text-red-600" aria-label="entfernen">✕</button>
          </li>
        ))}
      </ul>
      <button type="button" onClick={() => setOpen(true)} className={smallBtn}>+ Bild hinzufügen</button>
      {open && (
        <MediaPicker
          publicUrl={publicUrl}
          onClose={() => setOpen(false)}
          onPick={(item) => { onChange([...value, { url: item.url, alt: item.alt }]); setOpen(false); }}
        />
      )}
    </div>
  );
}

function LinkListField({ value, onChange, publicUrl }: { value: Array<{ label: string; url: string; image: string }>; onChange: (v: Array<{ label: string; url: string; image: string }>) => void; publicUrl: string }): JSX.Element {
  const set = (i: number, patch: Partial<{ label: string; url: string; image: string }>): void =>
    onChange(value.map((it, j) => (j === i ? { ...it, ...patch } : it)));
  return (
    <div className="mt-1.5 space-y-2">
      {value.map((item, i) => (
        <div key={i} className="well">
          <div className="grid gap-2 sm:grid-cols-2">
            <label className={labelClass}>Beschriftung
              <input value={item.label} onChange={(e) => set(i, { label: e.target.value })} maxLength={60} className={inputClass} />
            </label>
            <label className={labelClass}>Adresse
              <input value={item.url} onChange={(e) => set(i, { url: e.target.value })} maxLength={400} placeholder="https://…" className={inputClass} />
            </label>
          </div>
          <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
            <ImageField value={item.image} onChange={(v) => set(i, { image: v })} publicUrl={publicUrl} label="Logo" />
            <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))} className="btn btn-ghost btn-sm">Entfernen</button>
          </div>
        </div>
      ))}
      <button type="button" onClick={() => onChange([...value, { label: "", url: "", image: "" }])} className={smallBtn}>
        + Eintrag hinzufügen
      </button>
    </div>
  );
}

function Field({ field, value, onChange, channels, publicUrl, choices }: FieldProps): JSX.Element {
  switch (field.type) {
    case "category":
    case "post":
    case "episode": {
      const options = choices[field.type] ?? [];
      return (
        <label className={labelClass}>{field.label}
          <select value={typeof value === "string" ? value : ""} onChange={(e) => onChange(e.target.value)} className={inputClass}>
            <option value="">{CHOICE_EMPTY[field.type]}</option>
            {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          {options.length === 0 && <span className="field-hint block font-normal">Dafür gibt es noch nichts zur Auswahl.</span>}
        </label>
      );
    }
    case "boolean":
      return (
        <label className="flex cursor-pointer items-center gap-2.5 text-sm text-primary">
          <input type="checkbox" className="h-4 w-4 rounded" checked={value === true} onChange={(e) => onChange(e.target.checked)} />
          {field.label}
        </label>
      );
    case "select":
      return (
        <label className={labelClass}>{field.label}
          <select value={typeof value === "string" ? value : ""} onChange={(e) => onChange(e.target.value)} className={inputClass}>
            {(field.options ?? []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </label>
      );
    case "channel":
      return (
        <label className={labelClass}>{field.label}
          <select value={typeof value === "string" ? value : ""} onChange={(e) => onChange(e.target.value)} className={inputClass}>
            <option value="">Alle Kanäle</option>
            {channels.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
          </select>
        </label>
      );
    case "number":
      return (
        <label className={labelClass}>{field.label}
          <input type="number" min={field.min} max={field.max} value={typeof value === "number" ? value : ""}
            onChange={(e) => onChange(e.target.value === "" ? undefined : Number(e.target.value))} className={inputClass} />
        </label>
      );
    case "textarea":
    case "markdown":
      return (
        <label className={labelClass}>{field.label}
          <textarea rows={field.type === "markdown" ? 10 : 3} value={typeof value === "string" ? value : ""}
            onChange={(e) => onChange(e.target.value)} className={`${inputClass} font-mono text-[0.8rem] leading-relaxed`} />
        </label>
      );
    case "datetime":
      return (
        <label className={labelClass}>{field.label}
          <input type="datetime-local" value={typeof value === "string" && value ? toLocalInput(value) : ""}
            onChange={(e) => onChange(e.target.value ? new Date(e.target.value).toISOString() : "")} className={inputClass} />
        </label>
      );
    case "image":
      return (
        <div className={labelClass}>{field.label}
          <ImageField value={typeof value === "string" ? value : ""} onChange={onChange} publicUrl={publicUrl} label={field.label} />
        </div>
      );
    case "images":
      return (
        <div className={labelClass}>{field.label}
          <ImageListField value={Array.isArray(value) ? (value as Array<{ url: string; alt: string }>) : []} onChange={onChange} publicUrl={publicUrl} />
        </div>
      );
    case "links":
      return (
        <div className={labelClass}>{field.label}
          <LinkListField value={Array.isArray(value) ? (value as Array<{ label: string; url: string; image: string }>) : []} onChange={onChange} publicUrl={publicUrl} />
        </div>
      );
    default:
      return (
        <label className={labelClass}>{field.label}
          <input value={typeof value === "string" ? value : ""} placeholder={field.placeholder}
            onChange={(e) => onChange(e.target.value)} className={inputClass} />
        </label>
      );
  }
}

export function BlockForm({ fields, props, onChange, channels, publicUrl, choices }: {
  fields: FieldDef[];
  props: Props;
  onChange: (props: Props) => void;
  channels: Array<{ slug: string; name: string }>;
  publicUrl: string;
  choices?: FieldChoices;
}): JSX.Element {
  if (fields.length === 0) {
    return <p className="text-sm text-muted">Dieser Block hat nichts einzustellen.</p>;
  }
  return (
    <div className="space-y-4">
      {fields.map((field) => (
        <div key={field.name}>
          <Field
            field={field}
            value={props[field.name]}
            onChange={(value) => onChange({ ...props, [field.name]: value })}
            channels={channels}
            publicUrl={publicUrl}
            choices={choices ?? {}}
          />
          {field.hint && <p className="field-hint">{field.hint}</p>}
        </div>
      ))}
    </div>
  );
}
